import { FlowStep, FlowType } from 'src/frontend/constants/GettingStartedFlows';
import {
    FAULT_SIM_CONNECTION_ID,
    FAULT_SIM_TOUR_SNIPPET,
    FAULT_SIM_TOUR_SNIPPET_NAME,
} from 'src/frontend/constants/tour-snippets';
import { mutate } from 'swr';

import { DiagramSnippetHelpers } from '@diagram/helpers/DiagramSnippetHelpers';
import { DiagramSnippetService } from '@diagram/helpers/DiagramSnippetService';
import { ConnectionService } from '@diagram/services/ConnectionService';
import { gettingStartedTourState } from '@diagram/state/getting-started-tour';
import { SidebarTab } from '@diagram/state/sidebar';

import { DriverService } from 'services/drivers/DriverService';

export enum FaultSimTourStep {
    DRAG_SNIPPET = 'fault-sim-drag-snippet',
    CLICK_CONNECTION = 'fault-sim-click-connection',
    OPEN_FAULT_TAB = 'fault-sim-open-fault-tab',
    RUN_SIMULATION = 'fault-sim-run-simulation',
    FINISH = 'fault-sim-finish',
}

const STEP_SEQUENCE: FaultSimTourStep[] = [
    FaultSimTourStep.DRAG_SNIPPET,
    FaultSimTourStep.CLICK_CONNECTION,
    FaultSimTourStep.OPEN_FAULT_TAB,
    FaultSimTourStep.RUN_SIMULATION,
    FaultSimTourStep.FINISH,
];

const HIGHLIGHTS: Record<FaultSimTourStep, FlowStep | null> = {
    [FaultSimTourStep.DRAG_SNIPPET]: {
        selector: `[data-tour-snippet=${FlowType.FAULT_SIMULATIONS}]`,
        popover: {
            title: 'Drag this example onto your diagram',
            description:
                '<p>This example contains a small DC system you can use to explore fault simulations.</p><p>Drag it onto the diagram to get started.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [FaultSimTourStep.CLICK_CONNECTION]: {
        selector: `[data-connection-hit="${FAULT_SIM_CONNECTION_ID}"]`,
        popover: {
            title: 'Select a connection',
            description: '<p>Click on this connection to open its specifications.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [FaultSimTourStep.OPEN_FAULT_TAB]: {
        selector: `[data-sidebar-tab="${SidebarTab.FAULT_SIMULATION}"]`,
        popover: {
            title: 'Open Fault Simulations',
            description: '<p>Open the <strong>Fault Simulations</strong> tab.</p>',
            side: 'bottom',
            align: 'center',
        },
        popoverOffset: 10,
    },
    [FaultSimTourStep.RUN_SIMULATION]: {
        selector: '[data-tour-fault-run]',
        popover: {
            title: 'Run a fault simulation',
            description:
                '<p>A fault means creating a short circuit on a part of the line. The simulation shows how your protection responds to it.</p><p>Run a fault simulation at <strong>0% (start of the line)</strong> and <strong>100% (end of the line)</strong> to see how your protection responds along the cable.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [FaultSimTourStep.FINISH]: {
        selector: '[data-diagram-sidebar]',
        popover: {
            title: "That's the tour!",
            description:
                '<p>Nice work running your first fault simulation.</p><p>Want help interpreting the results? Click <strong>Request Support</strong> and our engineers will help you out.</p>',
            side: 'left',
            align: 'center',
        },
        isLastStep: true,
    },
};

const tourConnectionId = () => gettingStartedTourState.tourTargetConnectionId ?? FAULT_SIM_CONNECTION_ID;

const FaultSimDriverService = {
    start: async (initStep: FaultSimTourStep = FaultSimTourStep.DRAG_SNIPPET) => {
        DriverService.request(FlowType.FAULT_SIMULATIONS);

        try {
            if (initStep === FaultSimTourStep.DRAG_SNIPPET) {
                await FaultSimDriverService.addSnippet();
            }

            DriverService.setStep(FlowType.FAULT_SIMULATIONS, initStep);
        } catch (error) {
            console.error('Failed to start fault simulation tour', error);
            DriverService.cancel(FlowType.FAULT_SIMULATIONS);
        }
    },

    showHighlight: (step: FaultSimTourStep) => {
        const highlight = HIGHLIGHTS[step];

        if (!highlight) {
            return null;
        }

        const config =
            step === FaultSimTourStep.CLICK_CONNECTION
                ? { ...highlight, selector: `[data-connection-hit="${tourConnectionId()}"]` }
                : highlight;

        return DriverService.showHighlight(FlowType.FAULT_SIMULATIONS, config);
    },

    hideOnSnippetDrag: () => {
        return DriverService.hideOnDrag(() => FaultSimDriverService.showHighlight(FaultSimTourStep.DRAG_SNIPPET));
    },

    advance: () => {
        DriverService.advance(FlowType.FAULT_SIMULATIONS, STEP_SEQUENCE);
    },

    onSnippetDropped: (name: string, connectionIds: Record<string, string>) => {
        const isTourSnippet = name === FAULT_SIM_TOUR_SNIPPET_NAME;

        if (gettingStartedTourState.activeTourStep === FaultSimTourStep.DRAG_SNIPPET && isTourSnippet) {
            gettingStartedTourState.tourTargetConnectionId = connectionIds[FAULT_SIM_CONNECTION_ID] ?? null;

            FaultSimDriverService.advance();
        }
    },

    onFaultSimulationFinished: () => {
        if (gettingStartedTourState.activeTourStep === FaultSimTourStep.RUN_SIMULATION) {
            FaultSimDriverService.advance();
        }
    },

    onConnectionSelected: (connectionId: string) => {
        if (
            gettingStartedTourState.activeTourStep === FaultSimTourStep.CLICK_CONNECTION &&
            FaultSimDriverService.isTourConnection(connectionId)
        ) {
            FaultSimDriverService.advance();
        }
    },

    onSidebarTabChanged: () => {
        if (gettingStartedTourState.activeTourStep === FaultSimTourStep.OPEN_FAULT_TAB) {
            FaultSimDriverService.advance();
        }
    },

    isTourConnection: (connectionId: string) => {
        const connection = ConnectionService.getById(connectionId);

        if (!connection) {
            return false;
        }

        return connection.id === tourConnectionId();
    },

    addSnippet: async () => {
        try {
            const { docs } = await DiagramSnippetService.list();

            const exists = docs.some((snippet) => snippet.name === FAULT_SIM_TOUR_SNIPPET_NAME);

            if (exists) {
                return;
            }

            await DiagramSnippetService.create({
                name: FAULT_SIM_TOUR_SNIPPET_NAME,
                ...FAULT_SIM_TOUR_SNIPPET,
            });

            await mutate(DiagramSnippetHelpers.swr.list, undefined, { revalidate: true });
        } catch {
            DriverService.cancel(FlowType.FAULT_SIMULATIONS);
        }
    },
};

export { FaultSimDriverService };
